import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-before-an-exam",
  title: "A prayer before an exam — the night before and the morning of",
  h1: "A prayer before an exam",
  description:
    "A prayer for the hour before a test, four passages on wisdom and nerve, and the one thing prayer will not do for an exam.",
  targets: [
    "prayer before an exam",
    "prayer before a test",
    "prayer for students",
    "prayer for exam results",
  ],
  published: "2026-09-03",
  standfirst:
    "The prayer that helps before an exam is not the one about passing. It is the one about being able to think.",
  takeaways: [
    "Ask for recall and for calm, which are the two things nerves actually take away. Asking for a grade asks for something you have already largely determined.",
    "Joshua 1:9 is a command rather than a comfort — be strong and courageous, on the grounds of who is with you, not on the grounds of the odds.",
    "Prayer is not a substitute for revision, and no passage in Scripture suggests otherwise.",
  ],
  when: [
    "The night before, when you have done what you can do and stopping feels irresponsible.",
    "In the corridor outside, with everyone else revising loudly.",
    "In the first two minutes, when the paper is in front of you and your mind has gone blank.",
    "Afterwards, waiting for results, which is the longer and harder half.",
  ],
  prayer: [
    "God, I have done what I have done and there is not time to do more of it now.",
    "Give me back what I actually know. Not more than I learned — that would be a strange thing to ask for and I have not earned it. Just access to what is in there.",
    "Take enough of the nerves off that I can read the question properly, because most of what I have got wrong in my life I got wrong by not reading the question.",
    "If it goes badly, let it be a bad exam rather than a verdict on me. Those have felt like the same thing for as long as I can remember.",
    "And let me be decent to the people around me afterwards, whichever way it went for them and for me.",
  ],
  short: "God, give me back what I know, and enough calm to read the question.",
  honest: [
    "This prayer will not supply material you did not learn. Nothing in Scripture suggests it works that way, and treating it as though it might is a good way to conclude afterwards that God was not listening.",
    "It also does not guarantee a result. Faithful people fail exams, and the failure is not a message. It is an exam.",
    "And exam anxiety that stops you eating or sleeping for weeks is worth telling somebody about — a tutor, a parent, a doctor — rather than only praying about.",
  ],
  verses: [
    {
      verse: {
        text: "Haven't I commanded you? Be strong and courageous. Don't be afraid. Don't be dismayed, for Yahweh your God is with you wherever you go.",
        ref: "Joshua 1:9",
      },
      note: "Grammatically a command, not a reassurance, and said to a man taking over from Moses with an army waiting. The ground given for the courage is presence rather than probability — nobody tells Joshua it will be easy or that he will win. Wherever you go is the operative phrase: it includes the rooms where it goes badly.",
    },
    {
      verse: {
        text: "For Yahweh gives wisdom. Out of his mouth comes knowledge and understanding.",
        ref: "Proverbs 2:6",
      },
      note: "The verses on either side are about searching for it like silver and hidden treasure, which is Proverbs being characteristically unromantic: wisdom is given, and it is given to people digging for it. The giving and the work are not alternatives in this book, and an exam is a fairly literal case of that.",
    },
    {
      verse: {
        text: "But if any of you lacks wisdom, let him ask of God, who gives to all liberally and without reproach, and it will be given to him.",
        ref: "James 1:5",
      },
      note: "Without reproach is the useful half tonight. The fear underneath a last-minute prayer is often that it does not count because you left it too late — that the answer will come with a comment about your revision schedule. The verse specifically forecloses that.",
    },
    {
      verse: {
        text: "In nothing be anxious, but in everything, by prayer and petition with thanksgiving, let your requests be made known to God. And the peace of God, which surpasses all understanding, will guard your hearts and your thoughts in Christ Jesus.",
        ref: "Philippians 4:6–7",
      },
      note: "The word translated guard is a military one — a garrison posted around a town. What is being garrisoned is your thinking, which is exactly the thing an exam takes away first. Note that the peace arrives after the specific requests are made, not instead of making them.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Ask for access, not for content",
      id: "ask-for-access-not-for-content",
    },
    {
      type: "p",
      text: "Nerves do not usually remove what you learned. They block the route to it — which is why the answer arrives, intact and complete, twenty minutes after you have left the hall.",
    },
    {
      type: "p",
      text: "So the useful request is for the route rather than the material. Calm enough to read the question, calm enough to start writing, calm enough that the first blank does not become the second one.",
    },
    {
      type: "h2",
      text: "Stop revising the night before",
      id: "stop-revising-the-night-before",
    },
    {
      type: "p",
      text: "The last two hours of cramming reliably add less than the sleep they cost. This is well established and almost universally ignored, because stopping feels like not caring.",
    },
    {
      type: "note",
      text: "Praying and then going to bed is a better use of eleven o'clock than either praying or revising. Put the phone in another room while you are at it — the anxious hour of scrolling is the real thief here, not the revision.",
    },
    {
      type: "h2",
      text: "Afterwards is the longer part",
      id: "afterwards-is-the-longer-part",
    },
    {
      type: "p",
      text: "The exam takes two hours. The waiting takes six weeks, and it is where most of the damage is done — the replaying, the comparing, the conversations about what everybody else put.",
    },
    {
      type: "p",
      text: "That stretch is a {waiting problem} rather than an exam problem, and it responds to the same things any long wait does.",
      links: {
        "waiting problem": "/prayers/prayer-for-patience",
      },
    },
  ],
  faq: [
    {
      q: "What should I pray before an exam?",
      a: 'Ask for recall and for calm rather than for a grade: "God, give me back what I know, and enough calm to read the question." Those are the two things nerves actually take.',
    },
    {
      q: "Is it wrong to pray to pass a test?",
      a: "No, but notice what you are asking. The result is largely determined by work already done, and a prayer aimed only at the grade tends to be heard afterwards as a verdict on whether God was listening.",
    },
    {
      q: "Which Bible verse is good for students?",
      a: "Joshua 1:9 for nerve — it is a command rather than a reassurance, grounded in presence rather than odds — and James 1:5, which promises wisdom to anyone who asks, explicitly without a comment about their preparation.",
    },
  ],
  relatedPrayers: ["prayer-for-focus", "prayer-for-anxiety"],
  related: ["dopamine-detox-christian", "how-to-memorize-bible-verses"],
  relatedTopics: ["bible-verses-about-strength"],
  relatedPassages: ["philippians-4-6-7", "philippians-4-13"],
};
